// src/components/AdInterstitial.jsx
// Interstitial ad logic for FREE version

import { configService } from '../services/ConfigService';

class AdInterstitialManager {
  constructor() {
    this.lessonCount = 0;
    this.challengeCount = 0;
    this.initialized = false;
  }

  /**
   * Check if ads are enabled
   */
  isEnabled() {
    const config = configService.getConfig();
    return config?.monetization?.enabled && 
           config.monetization.type === 'ads' &&
           typeof window !== 'undefined' &&
           window.AdMob !== undefined;
  }

  /**
   * Initialize interstitial ads
   */
  async initialize() {
    if (!this.isEnabled()) {
      console.log('AdInterstitial: Ads disabled (PAID version or no AdMob)');
      return;
    }

    this.initialized = true;
    console.log('AdInterstitial: Initialized');
  }

  /**
   * Show interstitial after lesson completion
   */
  async showAfterLesson() {
    if (!this.isEnabled()) return;
    
    const config = configService.getConfig();
    const adConfig = config.monetization?.adConfig;
    
    if (!adConfig?.interstitialAfterLesson) return;
    
    this.lessonCount++;
    
    const frequency = adConfig.interstitialFrequency || 3;
    if (this.lessonCount >= frequency) {
      this.lessonCount = 0;
      await this.show();
    }
  }

  /**
   * Show interstitial after challenge completion
   */
  async showAfterChallenge() {
    if (!this.isEnabled()) return;
    
    const config = configService.getConfig();
    const adConfig = config.monetization?.adConfig;
    
    if (!adConfig?.interstitialAfterChallenge) return;
    
    this.challengeCount++;
    
    const frequency = adConfig.interstitialFrequency || 3;
    if (this.challengeCount >= frequency) {
      this.challengeCount = 0;
      await this.show();
    }
  }

  /**
   * Show interstitial ad
   */
  async show() {
    if (!this.isEnabled() || !window.AdMob) return;
    
    try {
      const config = configService.getConfig();
      const adUnits = config.monetization?.adUnits;
      const testMode = config.monetization?.adConfig?.testMode || false;
      
      if (!adUnits?.interstitial) {
        console.warn('AdInterstitial: No interstitial ad unit configured');
        return;
      }

      // Prepare interstitial
      await window.AdMob.prepareInterstitial({
        adId: adUnits.interstitial,
        isTesting: testMode,
        autoShow: false
      });
      
      // Show interstitial
      await window.AdMob.showInterstitial();
      
      console.log('AdInterstitial: Shown successfully');
    } catch (error) {
      console.error('AdInterstitial: Error showing ad:', error);
    }
  }

  /**
   * Show rewarded video ad
   * @param {Function} onRewarded - Callback when user earns reward
   * @param {Function} onError - Callback on error (optional)
   */
  async showRewarded(onRewarded, onError) {
    // For PAID version or if ads disabled, give reward directly
    if (!this.isEnabled()) {
      console.log('AdInterstitial: Rewarded ads disabled, granting reward');
      if (onRewarded) onRewarded();
      return;
    }

    if (!window.AdMob) {
      console.warn('AdInterstitial: AdMob not available');
      if (onRewarded) onRewarded();
      return;
    }

    try {
      const config = configService.getConfig();
      const adUnits = config.monetization?.adUnits;
      const testMode = config.monetization?.adConfig?.testMode || false;
      
      if (!adUnits?.rewarded) {
        console.warn('AdInterstitial: No rewarded ad unit configured');
        if (onRewarded) onRewarded();
        return;
      }

      // Set up reward listener
      const rewardHandler = () => {
        console.log('AdInterstitial: User earned reward');
        if (onRewarded) onRewarded();
        // Clean up listener
        window.AdMob.off('onRewardedVideoAdRewarded', rewardHandler);
      };

      // Set up close listener (in case user closes without watching)
      const closeHandler = () => {
        console.log('AdInterstitial: Rewarded video closed');
        window.AdMob.off('onRewardedVideoAdClosed', closeHandler);
      };

      // Set up error listener
      const errorHandler = (error) => {
        console.error('AdInterstitial: Rewarded video error:', error);
        if (onError) onError(error);
        else if (onRewarded) onRewarded(); // Give reward anyway on error
        window.AdMob.off('onRewardedVideoAdFailedToLoad', errorHandler);
      };

      // Register listeners
      window.AdMob.on('onRewardedVideoAdRewarded', rewardHandler);
      window.AdMob.on('onRewardedVideoAdClosed', closeHandler);
      window.AdMob.on('onRewardedVideoAdFailedToLoad', errorHandler);

      // Prepare rewarded video
      await window.AdMob.prepareRewardVideoAd({
        adId: adUnits.rewarded,
        isTesting: testMode,
        autoShow: false
      });
      
      // Show rewarded video
      await window.AdMob.showRewardVideoAd();
      
      console.log('AdInterstitial: Rewarded video shown');
    } catch (error) {
      console.error('AdInterstitial: Error showing rewarded video:', error);
      // Give reward anyway on error (better UX)
      if (onRewarded) onRewarded();
    }
  }

  /**
   * Check if rewarded video is ready
   */
  async isRewardedReady() {
    if (!this.isEnabled() || !window.AdMob) return false;
    
    try {
      // AdMob plugin may have isRewardedVideoAdReady method
      if (window.AdMob.isRewardedVideoAdReady) {
        return await window.AdMob.isRewardedVideoAdReady();
      }
      return true; // Assume ready if we can't check
    } catch (error) {
      console.error('AdInterstitial: Error checking rewarded status:', error);
      return false;
    }
  }

  /**
   * Preload rewarded video for faster display
   */
  async preloadRewarded() {
    if (!this.isEnabled() || !window.AdMob) return;

    try {
      const config = configService.getConfig();
      const adUnits = config.monetization?.adUnits;
      const testMode = config.monetization?.adConfig?.testMode || false;
      
      if (!adUnits?.rewarded) return;

      await window.AdMob.prepareRewardVideoAd({
        adId: adUnits.rewarded,
        isTesting: testMode,
        autoShow: false
      });
      
      console.log('AdInterstitial: Rewarded video preloaded');
    } catch (error) {
      console.error('AdInterstitial: Error preloading rewarded:', error);
    }
  }

  /**
   * Reset counters (useful for testing)
   */
  resetCounters() {
    this.lessonCount = 0;
    this.challengeCount = 0;
  }

  /**
   * Get current ad status (for debugging)
   */
  getStatus() {
    return {
      enabled: this.isEnabled(),
      initialized: this.initialized,
      lessonCount: this.lessonCount,
      challengeCount: this.challengeCount
    };
  }
}

// Export singleton instance
export const adInterstitial = new AdInterstitialManager();

// Also export the class for testing purposes
export default AdInterstitialManager;